import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import {
  AgentRunningNotice,
  SectionHeading,
  StatusBadge,
} from "@/accounts-payable/common";
import type { ApAgentRun, ApPurchaseDocument } from "@/accounts-payable/types";
import {
  AUTOMATED_RUN_BADGE,
  isAutomatedRun,
  isPendingReviewAction,
} from "@/accounts-payable/utils";
import { ActionGroup } from "./action-group";

/**
 * The agent's proposals for one document as one checklist in two groups: what
 * still needs review (open by default) and what's resolved (collapsed while
 * anything is pending, so the open work leads).
 */
export const AgentActionsSection = ({
  document,
  run,
  activeRun,
  loading,
  error,
  onViewAgentReview,
}: {
  document: Pick<ApPurchaseDocument, "id" | "status" | "document_type">;
  run: ApAgentRun | undefined;
  activeRun: ApAgentRun | undefined;
  /** Runs still loading — hold the empty state so it can't flash a false "none". */
  loading: boolean;
  /** Runs failed to load — say so rather than claim there are none. */
  error: boolean;
  onViewAgentReview?: (documentId: string) => void;
}) => {
  const kind = document.document_type ?? "invoice";
  const actions = run?.actions ?? [];
  const pending = actions.filter(isPendingReviewAction);
  const resolved = actions.filter((a) => !isPendingReviewAction(a));

  return (
    <Stack gap="md" fullWidth>
      <Stack
        direction="row"
        justify="between"
        align="center"
        gap="md"
        fullWidth
        className="flex-wrap"
      >
        <Stack direction="row" gap="sm" align="center" className="flex-wrap">
          <SectionHeading>Agent actions</SectionHeading>
          {run && isAutomatedRun(run) && (
            <StatusBadge meta={AUTOMATED_RUN_BADGE} />
          )}
        </Stack>
        {actions.length > 0 && onViewAgentReview && (
          <Button
            variant={pending.length > 0 ? "secondary" : "tertiary"}
            size="xsPill"
            onClick={() => onViewAgentReview(document.id)}
          >
            {pending.length > 0 ? "Review & approve" : "View all"}
          </Button>
        )}
      </Stack>

      {/* See the review page: the notice stands alongside a superseded run's
          proposals rather than being hidden by them. */}
      {activeRun && <AgentRunningNotice />}

      {error && actions.length === 0 && (
        <Typography textColor="tertiary">
          Couldn’t load agent actions. Please try again.
        </Typography>
      )}

      {actions.length === 0 && !activeRun && !loading && !error && (
        <Typography textColor="tertiary">
          {document.status === "matched"
            ? `No agent actions for this ${kind} yet.`
            : `The agent runs once the ${kind} is parsed and matched.`}
        </Typography>
      )}

      <ActionGroup label="Needs review" actions={pending} defaultOpen />

      <ActionGroup
        label="Resolved"
        actions={resolved}
        defaultOpen={pending.length === 0}
      />
    </Stack>
  );
};
